import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const CycleContext = createContext();

export const useCycle = () => {
    const context = useContext(CycleContext);
    if (!context) throw new Error('useCycle must be used within CycleProvider');
    return context;
};

export const CycleProvider = ({ children }) => {
    const { user } = useAuth();
    const [currentCycle, setCurrentCycle] = useState(null);
    const [cycleHistory, setCycleHistory] = useState([]);
    const [selectedCycleNumber, setSelectedCycleNumber] = useState(null);
    const [loading, setLoading] = useState(true);
    // paymentRequired = true ONLY when user has zero cycles (brand-new user)
    const [paymentRequired, setPaymentRequired] = useState(false);

    // ── Fetch current cycle ──
    const fetchCurrentCycle = useCallback(async () => {
        if (!user) { setLoading(false); return; }
        try {
            const { data } = await api.get('/cycle/current');
            setCurrentCycle(data.cycle);
            setPaymentRequired(false);
            if (!selectedCycleNumber) setSelectedCycleNumber(data.cycle.cycleNumber);
        } catch (error) {
            if (error.response?.status === 402) {
                setPaymentRequired(true);
                setCurrentCycle(null);
            } else {
                console.error('Error fetching current cycle:', error);
            }
        } finally {
            setLoading(false);
        }
    }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Fetch cycle history ──
    const fetchCycleHistory = useCallback(async () => {
        if (!user) return;
        try {
            const { data } = await api.get('/cycle/history');
            setCycleHistory(data.cycles || []);
        } catch (error) {
            console.error('Error fetching cycle history:', error);
        }
    }, [user]);

    // ── Start new cycle (after payment) ──
    const startNewCycle = async (paymentReference) => {
        try {
            const { data } = await api.post('/cycle/new', { paymentReference });
            setCurrentCycle(data.cycle);
            setPaymentRequired(false);
            setSelectedCycleNumber(data.cycle.cycleNumber);
            await fetchCycleHistory();
            window.dispatchEvent(new CustomEvent('cycle-changed', {
                detail: { cycleNumber: data.cycle.cycleNumber }
            }));
            return { success: true, cycle: data.cycle };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to start new cycle',
                remainingSections: error.response?.data?.remainingSections || []
            };
        }
    };

    // ── View a specific cycle ──
    const viewCycle = (cycleNumber) => {
        setSelectedCycleNumber(cycleNumber);
        window.dispatchEvent(new CustomEvent('cycle-changed', { detail: { cycleNumber } }));
    };

    const viewCurrentCycle = () => {
        if (currentCycle) viewCycle(currentCycle.cycleNumber);
    };

    // ── Initial load ──
    useEffect(() => {
        if (user) {
            fetchCurrentCycle();
            fetchCycleHistory();
        } else {
            setCurrentCycle(null);
            setCycleHistory([]);
            setPaymentRequired(false);
            setLoading(false);
        }
    }, [user, fetchCurrentCycle, fetchCycleHistory]);

    // ── Auto-refresh when journal entry is created ──
    useEffect(() => {
        const handler = () => fetchCurrentCycle();
        window.addEventListener('journal-entry-created', handler);
        return () => window.removeEventListener('journal-entry-created', handler);
    }, [fetchCurrentCycle]);

    // ── Keep api instance in sync ──
    useEffect(() => {
        api.setSelectedCycle(selectedCycleNumber);
    }, [selectedCycleNumber]);

    return (
        <CycleContext.Provider value={{
            currentCycle,
            cycleHistory,
            selectedCycleNumber,
            setSelectedCycleNumber,
            loading,
            paymentRequired,
            fetchCurrentCycle,
            fetchCycleHistory,
            startNewCycle,
            viewCycle,
            viewCurrentCycle,
            refreshCycle: fetchCurrentCycle,
            refreshHistory: fetchCycleHistory
        }}>
            {children}
        </CycleContext.Provider>
    );
};
